import { Navigate, Outlet, Route, Routes } from 'react-router-dom'
import { AuthProvider } from '../auth/AuthProvider'
import { AuthenticatedRoute } from '../auth/AuthenticatedRoute'
import { LoginExperiencePage } from '../auth/LoginExperiencePage'
import {
  AuthEmailConfirmationPage,
  ForgotPasswordExperiencePage,
  InviteAcceptanceExperiencePage,
  UpdatePasswordExperiencePage,
} from '../auth/AuthSupportPages'
import { TenantProvider } from '../tenancy/TenantProvider'
import { CustomerModuleRoute, CustomerServiceRoute } from '../tenancy/CustomerAccessRoutes'
import { InitiativeDetailPage, RoadmapPage, StrategyPage } from '../portfolio/PortfolioPages'
import { RegisterLandingPage } from '../register/RegisterLandingPage'
import { AiUseWizardPage } from '../register/AiUseWizardPage'
import { AiUseDetailRoute } from '../register/AiUseDetailRoute'
import { AssessmentPage } from '../governance/AssessmentPage'
import { ConfirmationTaskPage } from '../governance/ConfirmationTaskPage'
import { InformationRequestPage } from '../governance/InformationRequestPage'
import { ApprovalTaskPage } from '../governance/ApprovalTaskPage'
import { ActionsPage } from '../governance/ActionsPage'
import { RiskAcceptancePage } from '../risk/RiskAcceptancePage'
import { RiskManagementPage } from '../risk/RiskManagementPage'
import { RiskDetailPage, RiskMatrixPage, RiskRegisterPage } from '../risk/RiskPages'
import { CustomerEntryRoute } from '../onboarding/CustomerEntryRoute'
import { OnboardingSetupRoute } from '../onboarding/OnboardingSetupRoute'
import { AppShell } from './AppShell'
import { GovernanceOverviewPage } from './GovernanceOverviewPage'
import { ReportHistoryPage, ReportingSnapshotPage, ReportsPage } from '../reporting/ReportsPage'
import { ChangeIncidentDetailPage, ChangesIncidentsPage } from '../changes-incidents/ChangesIncidentsPages'
import {
  AdministrationPage,
  ProfilePage,
} from './WorkspacePages'

function TenantContextRoute() {
  return (
    <TenantProvider>
      <Outlet />
    </TenantProvider>
  )
}

export function App() {
  return (
    <AuthProvider>
      <Routes>
        <Route path="/auth/confirm" element={<AuthEmailConfirmationPage />} />
        <Route path="/auth/login" element={<LoginExperiencePage />} />
        <Route path="/auth/forgot-password" element={<ForgotPasswordExperiencePage />} />
        <Route path="/auth/update-password" element={<UpdatePasswordExperiencePage />} />
        <Route path="/invite/accept" element={<InviteAcceptanceExperiencePage />} />

        <Route element={<AuthenticatedRoute />}>
          <Route element={<TenantContextRoute />}>
            <Route path="/workspace/create" element={<Navigate to="/workspace/setup" replace />} />
            <Route path="/workspace/setup" element={<OnboardingSetupRoute />} />

            <Route element={<CustomerEntryRoute />}>
              <Route element={<AppShell />}>
                <Route index element={<CustomerServiceRoute><GovernanceOverviewPage /></CustomerServiceRoute>} />
                <Route path="/actions" element={<CustomerServiceRoute><ActionsPage /></CustomerServiceRoute>} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/administration" element={<AdministrationPage />} />

                <Route path="/discovery/*" element={<Navigate to="/ai-register" replace />} />

                <Route path="/strategy" element={<CustomerModuleRoute module="strategy_roadmap"><StrategyPage /></CustomerModuleRoute>} />
                <Route path="/strategy/initiatives/:initiativeId" element={<CustomerModuleRoute module="strategy_roadmap"><InitiativeDetailPage /></CustomerModuleRoute>} />
                <Route path="/roadmap" element={<CustomerModuleRoute module="strategy_roadmap"><RoadmapPage /></CustomerModuleRoute>} />
                <Route path="/reports" element={<CustomerServiceRoute><ReportsPage /></CustomerServiceRoute>} />
                <Route path="/reports/history" element={<CustomerServiceRoute><ReportHistoryPage /></CustomerServiceRoute>} />
                <Route path="/reports/history/:snapshotId" element={<CustomerServiceRoute><ReportingSnapshotPage /></CustomerServiceRoute>} />

                <Route path="/ai-register" element={<CustomerModuleRoute module="ai_register_product_library"><RegisterLandingPage /></CustomerModuleRoute>} />
                <Route path="/ai-product-library" element={<Navigate to="/ai-register" replace />} />
                <Route path="/ai-register/import" element={<Navigate to="/ai-register" replace />} />
                <Route path="/ai-register/add/:draftId" element={<CustomerModuleRoute module="ai_register_product_library"><AiUseWizardPage /></CustomerModuleRoute>} />
                <Route path="/ai-register/:useCaseId" element={<CustomerModuleRoute module="ai_register_product_library"><AiUseDetailRoute /></CustomerModuleRoute>} />
                <Route path="/changes-incidents" element={<CustomerModuleRoute module="ai_register_product_library"><ChangesIncidentsPage /></CustomerModuleRoute>} />
                <Route path="/changes-incidents/:recordType/:recordId" element={<CustomerModuleRoute module="ai_register_product_library"><ChangeIncidentDetailPage /></CustomerModuleRoute>} />
                <Route path="/assess/:useCaseId" element={<CustomerModuleRoute module="ai_register_product_library"><AssessmentPage /></CustomerModuleRoute>} />
                <Route path="/assess/confirm/:requirementId" element={<CustomerModuleRoute module="ai_register_product_library"><ConfirmationTaskPage /></CustomerModuleRoute>} />
                <Route path="/assess/information-request/:requestId" element={<CustomerModuleRoute module="ai_register_product_library"><InformationRequestPage /></CustomerModuleRoute>} />
                <Route path="/approve/:requirementId" element={<CustomerModuleRoute module="ai_register_product_library"><ApprovalTaskPage /></CustomerModuleRoute>} />

                <Route path="/ai-risks" element={<CustomerModuleRoute module="ai_register_product_library"><RiskRegisterPage /></CustomerModuleRoute>} />
                <Route path="/ai-risks/matrix" element={<CustomerModuleRoute module="ai_register_product_library"><RiskMatrixPage /></CustomerModuleRoute>} />
                <Route path="/ai-risks/matrix/configure" element={<Navigate to="/ai-risks/matrix" replace />} />
                <Route path="/ai-risks/report" element={<Navigate to="/ai-risks" replace />} />
                <Route path="/ai-risks/:riskId/manage" element={<CustomerModuleRoute module="ai_register_product_library"><RiskManagementPage /></CustomerModuleRoute>} />
                <Route path="/ai-risks/:riskId" element={<CustomerModuleRoute module="ai_register_product_library"><RiskDetailPage /></CustomerModuleRoute>} />
                <Route path="/risk-accept/:requestId" element={<CustomerModuleRoute module="ai_register_product_library"><RiskAcceptancePage /></CustomerModuleRoute>} />
              </Route>
            </Route>
          </Route>
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AuthProvider>
  )
}
